import React from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, AreaChart, Area } from "recharts";
import { TrendingUp, Users, UserPlus, CreditCard, Calendar, DollarSign } from "lucide-react";

const COLORS = ["#08707A", "#1CA3B0", "#003F72", "#3CD4E1", "#6B21A8", "#2D6A2E"];
const METHOD_COLORS = { stripe: "#6B21A8", paypal: "#1E40AF", bank: "#08707A", cash: "#2D6A2E" };
const METHOD_LABELS = { stripe: "Stripe", paypal: "PayPal", bank: "Transfer Bankar", cash: "Cash" };
const MONTH_NAMES = { "01": "Jan", "02": "Shk", "03": "Mar", "04": "Pri", "05": "Maj", "06": "Qer", "07": "Kor", "08": "Gsh", "09": "Sht", "10": "Tet", "11": "Nen", "12": "Dhj" };

const fmt = (n) => Number(n || 0).toFixed(2).replace(".", ",");

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow" data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`}>
    <div className="flex items-start justify-between">
      <div>
        <p className="text-[11px] font-bold uppercase tracking-wider text-[#828D9E] mb-1">{label}</p>
        <p className="text-2xl font-black font-['Inter_Tight']" style={{ color }}>{value}</p>
        {sub && <p className="text-xs text-[#828D9E] mt-1">{sub}</p>}
      </div>
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon size={18} style={{ color }} />
      </div>
    </div>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-lg px-4 py-2.5">
      <p className="text-xs font-bold text-[#003F72] mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="text-sm" style={{ color: p.color }}>
          {p.name}: <strong>{fmt(p.value)} €</strong>
        </p>
      ))}
    </div>
  );
};

const DashboardAnalytics = ({ data }) => {
  if (!data) return null;

  const monthData = (data.revenue_by_month || []).map(m => ({
    ...m,
    label: `${MONTH_NAMES[m.month?.split("-")[1]] || m.month?.split("-")[1]} ${m.month?.split("-")[0]?.slice(2)}`
  }));

  const methodData = (data.revenue_by_method || []).map(m => ({
    ...m,
    name: METHOD_LABELS[m.method] || m.method,
    color: METHOD_COLORS[m.method] || "#828D9E"
  }));

  const eventData = (data.revenue_by_event || []).slice(0, 8).map(e => ({
    ...e,
    shortTitle: e.title?.length > 25 ? e.title.slice(0, 25) + "..." : e.title
  }));

  const regsData = (data.registrations_by_event || []).slice(0, 8).map(r => ({
    ...r,
    shortTitle: r.title?.length > 25 ? r.title.slice(0, 25) + "..." : r.title
  }));

  return (
    <div data-testid="dashboard-analytics">
      <h2 className="text-lg font-bold font-['Inter_Tight'] mb-5 text-[#003F72]">Analitika</h2>

      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
        <StatCard icon={DollarSign} label="Te Ardhura" value={`${fmt(data.total_revenue)} €`} sub={`${data.paid_transactions} pagesa`} color="#08707A" />
        <StatCard icon={CreditCard} label="Ne Pritje" value={`${fmt(data.total_pending)} €`} sub={`${data.total_transactions - data.paid_transactions} transaksione`} color="#92400E" />
        <StatCard icon={TrendingUp} label="Transaksione" value={data.total_transactions} color="#6B21A8" />
        <StatCard icon={Users} label="Regjistrimet" value={data.total_registrations} color="#1CA3B0" />
        <StatCard icon={UserPlus} label="Leads" value={data.total_leads} color="#003F72" />
        <StatCard icon={Calendar} label="Evente" value={data.total_events} color="#065A63" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="chart-revenue-month">
          <h3 className="text-sm font-bold text-[#003F72] mb-4 uppercase tracking-wider">Te Ardhurat sipas Muajit</h3>
          {monthData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={monthData}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#08707A" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#08707A" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#828D9E" }} />
                <YAxis tick={{ fontSize: 11, fill: "#828D9E" }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="revenue" name="Te Ardhurat" stroke="#08707A" strokeWidth={2.5} fill="url(#colorRevenue)" />
              </AreaChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-[#828D9E] py-12 text-sm">Nuk ka te dhena per muajin.</p>}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="chart-revenue-method">
          <h3 className="text-sm font-bold text-[#003F72] mb-4 uppercase tracking-wider">Metodat e Pageses</h3>
          {methodData.length > 0 ? (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={methodData} cx="50%" cy="50%" innerRadius={60} outerRadius={95} dataKey="revenue" nameKey="name" paddingAngle={3}>
                  {methodData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(v) => `${fmt(v)} €`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-[#828D9E] py-12 text-sm">Nuk ka te dhena.</p>}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="chart-revenue-event">
          <h3 className="text-sm font-bold text-[#003F72] mb-4 uppercase tracking-wider">Te Ardhurat sipas Eventit</h3>
          {eventData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={eventData} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#828D9E" }} />
                <YAxis type="category" dataKey="shortTitle" width={140} tick={{ fontSize: 10, fill: "#4A5568" }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" name="Te Ardhurat" radius={[0, 6, 6, 0]} barSize={22}>
                  {eventData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-[#828D9E] py-12 text-sm">Nuk ka te dhena.</p>}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="chart-regs-event">
          <h3 className="text-sm font-bold text-[#003F72] mb-4 uppercase tracking-wider">Regjistrimet sipas Eventit</h3>
          {regsData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={regsData} layout="vertical" margin={{ left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis type="number" tick={{ fontSize: 11, fill: "#828D9E" }} allowDecimals={false} />
                <YAxis type="category" dataKey="shortTitle" width={140} tick={{ fontSize: 10, fill: "#4A5568" }} />
                <Tooltip formatter={(v) => `${v} regjistrues`} />
                <Bar dataKey="count" name="Regjistrimet" radius={[0, 6, 6, 0]} barSize={22}>
                  {regsData.map((_, i) => <Cell key={i} fill={COLORS[(i + 2) % COLORS.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : <p className="text-center text-[#828D9E] py-12 text-sm">Nuk ka te dhena.</p>}
        </div>
      </div>

      {data.lead_sources?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm" data-testid="chart-lead-sources">
          <h3 className="text-sm font-bold text-[#003F72] mb-4 uppercase tracking-wider">Burimet e Lead-eve</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {data.lead_sources.map((s, i) => (
              <div key={i} className="rounded-lg p-4 text-center" style={{ backgroundColor: `${COLORS[i % COLORS.length]}10` }}>
                <p className="text-xl font-black font-['Inter_Tight']" style={{ color: COLORS[i % COLORS.length] }}>{s.count}</p>
                <p className="text-xs text-[#4A5568] mt-1 font-medium">{s.source || "Te tjera"}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardAnalytics;
